import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box, Typography, Paper, Grid, Button, TextField, Alert, IconButton,
    CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Stack, Chip, InputAdornment, Autocomplete, Checkbox
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    CloudUpload as CloudUploadIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Calculate as CalculateIcon,
    Inventory as InventoryIcon,
    Business as BusinessIcon,
    CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
    CheckBox as CheckBoxIcon
} from '@mui/icons-material';

// API Configuration
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = {
    get: (endpoint) => axios.get(`${API_URL}${endpoint}`),
    post: (endpoint, data) => axios.post(`${API_URL}${endpoint}`, data),
};

const getConfig = (key) => api.get(`/config/${key}`);
const saveConfig = (key, data) => api.post(`/config/${key}`, data);

const PESOS_PRODUCAO_KEY = 'pesos_producao_config';

// File upload helper
const uploadPlanilhaItens = async (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const text = e.target.result;
                const rows = text.split('\n').map(row => row.trim()).filter(row => row);
                const headers = rows[0].split(',').map(h => h.trim().toLowerCase());

                if (!headers.includes('código') && !headers.includes('codigo')) {
                    reject("Planilha inválida: Coluna 'Código' não encontrada.");
                    return;
                }

                const items = [];
                for (let i = 1; i < rows.length; i++) {
                    const cols = rows[i].split(',');
                    if (cols.length < 2) continue;

                    items.push({
                        codigo: cols[0].trim(),
                        descricao: cols[1] ? cols[1].trim() : `Item ${cols[0]}`,
                        peso: cols[2] ? parseFloat(cols[2]) : 1
                    });
                }
                resolve(items);
            } catch (error) {
                reject("Erro ao processar arquivo: " + error.message);
            }
        };
        reader.onerror = () => reject("Erro ao ler arquivo.");
        reader.readAsText(file);
    });
};

const RateioPorItem = () => {
    const [itens, setItens] = useState([]);
    const [itensPendentes, setItensPendentes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [centrosCusto, setCentrosCusto] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Form states
    const [novoCodigo, setNovoCodigo] = useState('');
    const [novoDescricao, setNovoDescricao] = useState('');
    const [novoPeso, setNovoPeso] = useState(1);
    const [fileSelected, setFileSelected] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    // Debug: Log when itensPendentes changes
    useEffect(() => {
        console.log('🔄 [RateioPorItem] itensPendentes state updated:', itensPendentes);
        console.log(`📊 [RateioPorItem] itensPendentes.length = ${itensPendentes.length}`);
    }, [itensPendentes]);

    const fetchData = async () => {
        setLoading(true);
        console.log('🔍 [RateioPorItem] Fetching data...');
        try {
            const [resItens, resPendentes, resCentros] = await Promise.all([
                getConfig(PESOS_PRODUCAO_KEY),
                getConfig('itens_pendentes_producao'),
                getConfig('centros_custo')
            ]);

            console.log('📦 [RateioPorItem] Itens response:', resItens.data);
            console.log('⚠️ [RateioPorItem] Pendentes response:', resPendentes.data);

            let fetchedItens = [];
            if (resItens.data && Array.isArray(resItens.data)) {
                console.log(`✅ [RateioPorItem] Setting ${resItens.data.length} itens`);
                fetchedItens = resItens.data;
                setItens(fetchedItens);
            }

            if (resPendentes.data && Array.isArray(resPendentes.data)) {
                const itensSet = new Set(fetchedItens.map(i => String(i.codigo).trim().toUpperCase()));
                const filteredPendentes = resPendentes.data.filter(p => !itensSet.has(String(p.codigo).trim().toUpperCase()));
                console.log(`⚠️ [RateioPorItem] Setting ${filteredPendentes.length} pending items (filtered from ${resPendentes.data.length})`);
                setItensPendentes(filteredPendentes);
            } else {
                console.warn('❌ [RateioPorItem] Pendentes data is not an array:', resPendentes.data);
            }

            if (resCentros.data && Array.isArray(resCentros.data)) {
                setCentrosCusto(resCentros.data.map(c => c.nome.toUpperCase()).sort());
            }
        } catch (err) {
            console.error('❌ [RateioPorItem] Erro ao carregar dados:', err);
            setError('Erro ao carregar dados. Verifique a conexão.');
        } finally {
            setLoading(false);
            console.log('🏁 [RateioPorItem] Loading complete');
        }
    };

    const handleAddItem = () => {
        if (!novoCodigo || !novoDescricao) {
            setError('Preencha código e descrição.');
            return;
        }
        const novoItem = { 
            codigo: novoCodigo, 
            descricao: novoDescricao, 
            peso: parseFloat(novoPeso) || 1,
            centros_permitidos: []
        };
        setItens([...itens, novoItem]);
        setNovoCodigo('');
        setNovoDescricao('');
        setNovoPeso(1);
        setSuccess('Item adicionado!');
        setTimeout(() => setSuccess(null), 3000);
    };

    const handleUpdateItem = (index, field, value, isPendente = false) => {
        if (isPendente) {
            const newPendentes = [...itensPendentes];
            newPendentes[index][field] = field === 'peso' ? (parseFloat(value) || 0) : value;
            setItensPendentes(newPendentes);
        } else {
            const newItens = [...itens];
            newItens[index][field] = field === 'peso' ? (parseFloat(value) || 0) : value;
            setItens(newItens);
        }
    };

    const handleRemoveItem = (index) => {
        if (!window.confirm('Tem certeza que deseja remover este item?')) return;
        const newItens = itens.filter((_, i) => i !== index);
        setItens(newItens);
    };

    const handleFileChange = (e) => {
        setFileSelected(e.target.files[0]);
    };

    const handleImport = async () => {
        if (!fileSelected) {
            setError('Selecione um arquivo CSV primeiro.');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const importedItems = await uploadPlanilhaItens(fileSelected);
            const itensMap = new Map(itens.map(i => [i.codigo.toString(), i]));

            let novosCount = 0;
            let atualizadosCount = 0;

            importedItems.forEach(imp => {
                const codigoStr = imp.codigo.toString();
                if (itensMap.has(codigoStr)) {
                    itensMap.set(codigoStr, { ...itensMap.get(codigoStr), ...imp });
                    atualizadosCount++;
                } else {
                    itensMap.set(codigoStr, imp);
                    novosCount++;
                }
            });

            const listaFinal = Array.from(itensMap.values());
            setItens(listaFinal);
            setSuccess(`Importação concluída! ${novosCount} novos itens, ${atualizadosCount} atualizados.`);
            setFileSelected(null);

        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
            setTimeout(() => setSuccess(null), 4000);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            await saveConfig(PESOS_PRODUCAO_KEY, itens);
            setSuccess('Fichas técnicas salvas com sucesso!');
        } catch (err) {
            setError('Erro ao salvar no banco de dados.');
        } finally {
            setSaving(false);
            setTimeout(() => setSuccess(null), 3000);
        }
    };

    const handleApprovePendente = (index) => {
        const item = itensPendentes[index];
        if (itens.some(i => i.codigo === item.codigo)) {
            alert('Este item já está cadastrado na lista principal.');
            return;
        }
        setItens([...itens, item]);
        setItensPendentes(itensPendentes.filter((_, i) => i !== index));
    };

    if (loading && !itens.length && !itensPendentes.length) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress />
        </Box>
    );

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', display: 'flex', alignItems: 'center' }}>
                <InventoryIcon sx={{ mr: 2, color: 'primary.main' }} /> Gestão de Fichas Técnicas (Itens de Produção)
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                Cadastre e gerencie as Unidades de Custo (UC) para cada item de produção.
                Estes pesos serão usados para distribuir os custos dentro de cada centro.
            </Typography>

            {/* Debug Info */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Debug: Pendentes count: {itensPendentes.length}
            </Typography>

            {/* Mensagens */}
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

            <Grid container spacing={4}>

                {/* COLUNA ESQUERDA: Cadastro Manual e Importação */}
                <Grid item xs={12} lg={4}>
                    <Stack spacing={4}>

                        {/* Card Cadastro Manual */}
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>Novo Item Manual</Typography>
                            <Stack spacing={2}>
                                <TextField
                                    label="Código"
                                    value={novoCodigo}
                                    onChange={e => setNovoCodigo(e.target.value)}
                                    placeholder="Ex: 10101012"
                                    size="small"
                                    fullWidth
                                />
                                <TextField
                                    label="Descrição"
                                    value={novoDescricao}
                                    onChange={e => setNovoDescricao(e.target.value)}
                                    placeholder="Ex: Diária UTI"
                                    size="small"
                                    fullWidth
                                />
                                <TextField
                                    label="Peso (UC)"
                                    type="number"
                                    value={novoPeso}
                                    onChange={e => setNovoPeso(e.target.value)}
                                    placeholder="Ex: 1.00"
                                    size="small"
                                    fullWidth
                                    inputProps={{ min: 0.01, step: 0.01 }}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleAddItem}
                                    startIcon={<AddIcon />}
                                >
                                    Adicionar Item
                                </Button>
                            </Stack>
                        </Paper>

                        {/* Card Importação CSV */}
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>Importar via Planilha (CSV)</Typography>
                            <Stack spacing={2}>
                                <Button
                                    variant="outlined"
                                    component="label"
                                    fullWidth
                                    startIcon={<CloudUploadIcon />}
                                >
                                    {fileSelected ? fileSelected.name : "Selecionar Arquivo CSV"}
                                    <input
                                        type="file"
                                        hidden
                                        accept=".csv"
                                        onChange={handleFileChange}
                                    />
                                </Button>
                                <Button
                                    variant="contained"
                                    color="info"
                                    onClick={handleImport}
                                    disabled={!fileSelected || loading}
                                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                                >
                                    {loading ? 'Carregando...' : 'Importar Dados'}
                                </Button>
                            </Stack>
                        </Paper>

                        {/* Seção de Pendências */}
                        {itensPendentes.length > 0 && (
                            <Paper sx={{ p: 0, overflow: 'hidden', border: 1, borderColor: 'warning.main' }}>
                                <Box sx={{ p: 2, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                                    <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                                        <WarningIcon sx={{ mr: 1 }} /> Itens Importados Sem Ficha Técnica
                                    </Typography>
                                </Box>
                                <TableContainer sx={{ maxHeight: 300 }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Código</TableCell>
                                                <TableCell>Descrição</TableCell>
                                                <TableCell>Peso</TableCell>
                                                <TableCell>Ação</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {itensPendentes.map((item, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell>{item.codigo}</TableCell>
                                                    <TableCell>{item.descricao}</TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            type="number"
                                                            value={item.peso}
                                                            onChange={(e) => handleUpdateItem(idx, 'peso', e.target.value, true)}
                                                            size="small"
                                                            sx={{ width: 80 }}
                                                            inputProps={{ style: { textAlign: 'center' } }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <IconButton
                                                            color="success"
                                                            onClick={() => handleApprovePendente(idx)}
                                                            size="small"
                                                        >
                                                            <CheckCircleIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        )}

                    </Stack>
                </Grid>

                {/* COLUNA DIREITA: Lista de Itens Cadastrados */}
                <Grid item xs={12} lg={8}>
                    <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Itens Cadastrados ({itens.length})
                        </Typography>
                        <TableContainer sx={{ flexGrow: 1, maxHeight: 600, mb: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell width="15%">Código</TableCell>
                                        <TableCell width="35%">Descrição</TableCell>
                                        <TableCell width="10%">Peso (UC)</TableCell>
                                        <TableCell width="30%">Centros Permitidos (Opcional)</TableCell>
                                        <TableCell width="10%" align="center">Ações</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {itens.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                                Nenhum item cadastrado.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        itens.map((item, index) => (
                                            <TableRow key={index} hover>
                                                <TableCell>{item.codigo}</TableCell>
                                                <TableCell>
                                                    <TextField
                                                        value={item.descricao}
                                                        onChange={(e) => handleUpdateItem(index, 'descricao', e.target.value)}
                                                        size="small"
                                                        fullWidth
                                                        variant="standard"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        type="number"
                                                        value={item.peso}
                                                        onChange={(e) => handleUpdateItem(index, 'peso', e.target.value)}
                                                        size="small"
                                                        fullWidth
                                                        variant="standard"
                                                        inputProps={{ min: 0.01, step: 0.01, style: { textAlign: 'center' } }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Autocomplete
                                                        multiple
                                                        options={centrosCusto}
                                                        value={item.centros_permitidos || []}
                                                        onChange={(e, newValue) => handleUpdateItem(index, 'centros_permitidos', newValue)}
                                                        renderInput={(params) => (
                                                            <TextField {...params} variant="standard" placeholder="Todos" size="small" />
                                                        )}
                                                        renderTags={(value, getTagProps) =>
                                                            value.map((option, index) => (
                                                                <Chip 
                                                                    label={option} 
                                                                    {...getTagProps({ index })} 
                                                                    size="small" 
                                                                    color="primary"
                                                                    variant="outlined"
                                                                />
                                                            ))
                                                        }
                                                        disableCloseOnSelect
                                                        renderOption={(props, option, { selected }) => (
                                                            <li {...props}>
                                                                <Checkbox
                                                                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                                                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                                                                    style={{ marginRight: 8 }}
                                                                    checked={selected}
                                                                />
                                                                {option}
                                                            </li>
                                                        )}
                                                        sx={{ 
                                                            '& .MuiAutocomplete-inputRoot': { fontSize: '0.75rem' }
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        onClick={() => handleRemoveItem(index)}
                                                        color="error"
                                                        size="small"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                            <Button
                                variant="contained"
                                color="success"
                                onClick={handleSave}
                                disabled={saving}
                                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                            >
                                {saving ? 'Salvando...' : 'Salvar Fichas Técnicas'}
                            </Button>

                            <Button
                                variant="contained"
                                color="primary"
                                onClick={async () => {
                                    if (!window.confirm('Deseja recalcular os custos do último mês utilizando os novos pesos? Isso pode levar alguns instantes.')) return;
                                    setSaving(true);
                                    try {
                                        await api.post('/reprocessar_ultimo_mes');
                                        setSuccess('Recálculo iniciado! Verifique o Dashboard em instantes.');
                                    } catch (err) {
                                        setError('Erro ao iniciar recálculo: ' + (err.response?.data?.mensagem || err.message));
                                    } finally {
                                        setSaving(false);
                                        setTimeout(() => setSuccess(null), 5000);
                                    }
                                }}
                                disabled={saving}
                                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <CalculateIcon />}
                            >
                                Recalcular Custos (Aplicar Pesos)
                            </Button>
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default RateioPorItem;